'use client';

import type { InteractionType } from '~/model/InteractionType';
import type { ProjectType } from '~/model/ProjectType';
import { VpAsset, VpButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { getShareUrl } from '@/app/actions/server/seo';
import { usePathname } from '@/i18n/routing';
import { GenericDialog } from '~/components/commons/genericDialog/GenericDialog';
import { checkIsProject } from '~/components/Project/utils';
import { entityRoutes } from '~/const/appConst';

export const ShareDialog = ({
  open,
  entity,
  handleShareDialogOpen,
}: {
  entity?: InteractionType | ProjectType;
  handleShareDialogOpen: (opening: boolean) => void;
  open: boolean;
}) => {
  const t = useTranslations('SHARE_DIALOG');
  const pathname = usePathname();

  const [sharedUrl, setSharedUrl] = useState<string>();

  const isProject = checkIsProject(entity);
  const entityTitle = encodeURIComponent(entity?.title);
  // If entity to share is a project, force the shared url
  const urlToShare = isProject ? `/${entityRoutes.project}/${entity.id}` : pathname;

  const fetchShareUrl = useCallback(async () => {
    const url = new URL(await getShareUrl(urlToShare));
    url.searchParams.set('utm_medium', 'share_link');
    setSharedUrl(encodeURIComponent(url.href));
  }, [urlToShare]);

  useEffect(() => {
    fetchShareUrl();
  }, [fetchShareUrl]);

  const content = (
    <div className="popin share-popin">
      <h3 className="share-popin__introduction vp-title-s">
        {t.rich('TITLE')}
      </h3>
      <ul>
        <li>
          <VpButton
            data-testid="facebook-link"
            href={`https://www.facebook.com/sharer/sharer.php?u=${sharedUrl}&quote=${entityTitle}`}
            title={t('SHARE_FACEBOOK')}
            target="_blank"
            rel="noopener noreferrer"
            variant="tertiary"
            size="small"
            startSlot={<VpAsset name="social-facebook-black" />}
          >
            {t('SHARE_FACEBOOK')}
          </VpButton>
        </li>
        <li>
          <VpButton
            data-testid="twitter-link"
            href={`https://twitter.com/intent/tweet?url=${sharedUrl}&text=${entityTitle}`}
            title={t('SHARE_X')}
            target="_blank"
            rel="noopener noreferrer"
            variant="tertiary"
            size="small"
            startSlot={<VpAsset name="social-x-black" />}
          >
            {t('SHARE_X')}
          </VpButton>
        </li>
      </ul>
    </div>
  );

  return (
    <GenericDialog
      isOpen={open}
      isClosable
      maskClosable
      onClose={() => handleShareDialogOpen(false)}
      content={content}
      hasFooter={false}
    />
  );
};
